export const LISTING_MEDIA_PUBLIC_HOSTS = [
    "media.wags.com",
    "media.staging.wags.com",
] as const;

export const LISTING_IMAGE_VARIANT_WIDTHS = [32, 96, 256, 640, 1080] as const;

export const LISTING_IMAGE_MASTER_WIDTH = 1200;

export const LISTING_IMAGE_DELIVERY_WIDTHS = [
    ...LISTING_IMAGE_VARIANT_WIDTHS,
    LISTING_IMAGE_MASTER_WIDTH,
] as const;

export type ListingImageVariantWidth =
    (typeof LISTING_IMAGE_VARIANT_WIDTHS)[number];

const LISTING_MEDIA_PATH_PATTERN =
    /^\/listing-media\/[0-9a-f]{64}\.[a-z0-9]{1,8}$/i;

const LISTING_MEDIA_VARIANT_PATH_PATTERN = new RegExp(
    `^/listing-media/[0-9a-f]{64}\\.w(?:${LISTING_IMAGE_VARIANT_WIDTHS.join("|")})\\.webp$`,
    "i",
);

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
    avif: "image/avif",
    bmp: "image/bmp",
    gif: "image/gif",
    heic: "image/heic",
    heif: "image/heif",
    jpeg: "image/jpeg",
    jfif: "image/jpeg",
    jpg: "image/jpeg",
    m4v: "video/mp4",
    mov: "video/quicktime",
    mp4: "video/mp4",
    mpeg: "video/mpeg",
    mpg: "video/mpeg",
    ogv: "video/ogg",
    png: "image/png",
    svg: "image/svg+xml",
    tif: "image/tiff",
    tiff: "image/tiff",
    webm: "video/webm",
    webp: "image/webp",
};

function getPathname(input: string) {
    try {
        return new URL(input).pathname;
    } catch {
        return input.split(/[?#]/, 1)[0] ?? "";
    }
}

function getExtension(input: string | null | undefined) {
    if (!input) {
        return null;
    }

    const pathname = getPathname(input).toLowerCase();
    const filename = pathname.split("/").pop() ?? "";
    const extension = filename.split(".").pop();

    if (!extension || extension === filename) {
        return null;
    }

    return extension;
}

export function normalizeMimeType(mimeType: string | null | undefined) {
    const normalized = mimeType?.trim().toLowerCase();
    return normalized ? normalized : null;
}

export function inferMimeTypeFromUrl(url: string | null | undefined) {
    const extension = getExtension(url);
    if (!extension) {
        return null;
    }

    return MIME_TYPE_BY_EXTENSION[extension] ?? null;
}

export function resolveMimeType(
    mimeType: string | null | undefined,
    url: string | null | undefined,
) {
    return normalizeMimeType(mimeType) ?? inferMimeTypeFromUrl(url);
}

export function normalizeRemoteMediaUrl(
    url: string | null | undefined,
): string | null {
    const trimmed = url?.trim();
    if (!trimmed) {
        return null;
    }

    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
        return trimmed;
    }

    if (trimmed.startsWith("//")) {
        return `https:${trimmed}`;
    }

    if (trimmed.startsWith("/")) {
        return trimmed;
    }

    return `https://${trimmed}`;
}

export function isImageMimeType(mimeType: string | null | undefined) {
    return normalizeMimeType(mimeType)?.startsWith("image/") === true;
}

export function isVideoMimeType(mimeType: string | null | undefined) {
    return normalizeMimeType(mimeType)?.startsWith("video/") === true;
}

export function isVideoMedia(input: {
    mimeType?: string | null;
    url?: string | null;
}) {
    return isVideoMimeType(resolveMimeType(input.mimeType, input.url));
}

function isListingMediaHostedUrl(
    url: string | null | undefined,
    pathPattern: RegExp,
) {
    const normalized = normalizeRemoteMediaUrl(url);
    if (!normalized) {
        return false;
    }

    try {
        const parsed = new URL(normalized);
        if (parsed.protocol !== "https:") {
            return false;
        }
        if (!pathPattern.test(parsed.pathname)) {
            return false;
        }
        const host = parsed.hostname.toLowerCase();
        return (LISTING_MEDIA_PUBLIC_HOSTS as readonly string[]).includes(host);
    } catch {
        return false;
    }
}

export function isListingImageVariantWidth(
    width: number,
): width is ListingImageVariantWidth {
    return (LISTING_IMAGE_VARIANT_WIDTHS as readonly number[]).includes(width);
}

export function isPreoptimizedMediaUrl(url: string | null | undefined) {
    return isListingMediaHostedUrl(url, LISTING_MEDIA_PATH_PATTERN);
}

export function isListingMediaVariantUrl(url: string | null | undefined) {
    return isListingMediaHostedUrl(url, LISTING_MEDIA_VARIANT_PATH_PATTERN);
}

export function isStoredListingMediaUrl(url: string | null | undefined) {
    return isPreoptimizedMediaUrl(url) || isListingMediaVariantUrl(url);
}

export function listingMediaDeliveryUrl(
    url: string | null | undefined,
    width: number,
): string | null {
    const normalized = normalizeRemoteMediaUrl(url);
    if (!normalized || !isPreoptimizedMediaUrl(normalized)) {
        return null;
    }

    let parsed: URL;
    try {
        parsed = new URL(normalized);
    } catch {
        return null;
    }
    if (!parsed.pathname.toLowerCase().endsWith(".webp")) {
        return null;
    }
    if (width === LISTING_IMAGE_MASTER_WIDTH) {
        return normalized;
    }
    if (!isListingImageVariantWidth(width)) {
        return null;
    }

    const variant = new URL(normalized);
    variant.pathname = parsed.pathname.replace(/\.webp$/i, `.w${width}.webp`);
    return variant.toString();
}

const trustedListingPhotoProviders = [
    ["eytelnof6drqenaz.public.blob.vercel-storage.com", "/dog/"],
    ["dbw3zep4prcju.cloudfront.net", "/animal/"],
    ["cdn.marketplace.akc.org", "/media/"],
] as const;

export function isTrustedListingPhotoUrl(url: string | null | undefined) {
    if (!url?.startsWith("https://")) return false;
    try {
        const parsed = new URL(url);
        const authority = url.slice("https://".length).split(/[/?#]/u, 1)[0];
        if (!authority || parsed.hash) return false;
        if (LISTING_MEDIA_PUBLIC_HOSTS.some((host) => host === authority))
            return (
                !parsed.search &&
                isPreoptimizedMediaUrl(url) &&
                isImageMimeType(inferMimeTypeFromUrl(url))
            );
        if (parsed.search) {
            const versionId = parsed.searchParams.get("versionId");
            if (
                authority !== "dbw3zep4prcju.cloudfront.net" ||
                parsed.searchParams.size !== 1 ||
                !versionId ||
                versionId.length > 128
            )
                return false;
        }
        return trustedListingPhotoProviders.some(
            ([host, prefix]) =>
                authority === host &&
                parsed.pathname.startsWith(prefix) &&
                parsed.pathname.length > prefix.length,
        );
    } catch {
        return false;
    }
}

export function inferExtensionFromMimeType(
    mimeType: string | null | undefined,
) {
    const normalized = normalizeMimeType(mimeType);
    if (!normalized) {
        return null;
    }

    return (
        Object.entries(MIME_TYPE_BY_EXTENSION).find(
            ([, candidateMimeType]) => candidateMimeType === normalized,
        )?.[0] ?? null
    );
}
